import Highcharts from 'highcharts';
import { DimensionNames } from '../../../../Constants/DimensionNames';

export const KPI_ATTRIBUTE_IDS = [9001, 9002, 9003, 9004, 9005, 9006];
export const HISTORIC_AUM_ATTRIBUTE_ID = 9007;
export const HISTORIC_NET_FLOWS_ATTRIBUTE_ID = 9008;
export const HISTORIC_NET_REVENUES_ATTRIBUTE_ID = 9009;
export const VALUE_DECIMALS = 1;

export const BAR_CHART_DEFINITIONS = [
  {
    id: HISTORIC_AUM_ATTRIBUTE_ID,
    title: 'AUM',
    yAxisTitle: 'AUM',
    metricLabel: 'AUM - end of year',
    seriesName: 'AUM - end of year',
    minY: 0,
  },
  {
    id: HISTORIC_NET_FLOWS_ATTRIBUTE_ID,
    title: 'Net Flows',
    yAxisTitle: 'Net Flows',
    metricLabel: 'Net Flows',
    seriesName: 'Net Flows',
    allowNegative: true,
  },
  {
    id: HISTORIC_NET_REVENUES_ATTRIBUTE_ID,
    title: 'Revenues',
    yAxisTitle: 'Net revenues',
    metricLabel: 'Net revenues',
    seriesName: 'Net revenues',
    minY: 0,
  },
];

export const KPI_DEFINITIONS = [
  { id: 9001, name: 'AUM', hoverName: 'AUM - End of Year' },
  { id: 9002, name: 'AUM CAGR', truncateDisplay: true },
  { id: 9003, name: 'Net Flows' },
  { id: 9004, name: 'Net Flow Effect', hoverName: 'Net Flows as % of BoY AUM', truncateDisplay: true },
  { id: 9005, name: 'Net Revenues' },
  { id: 9006, name: 'Net Revenue Margin (bps)', truncateDisplay: true },
];

export const KPI_VALUE_FORMAT_BY_ID = {
  9001: 'bn',
  9002: 'percent',
  9003: 'bn',
  9004: 'percent',
  9005: 'bn',
  9006: 'bps',
};

export const DEFAULT_BAR_CHART_ID = HISTORIC_AUM_ATTRIBUTE_ID;

export const KPI_IDS_WITH_CURRENCY_IN_HEADING = new Set([9001, 9003, 9005]);

const CURRENCY_SYMBOL_BY_CODE = {
  USD: '$',
  EUR: '€',
  GBP: '£',
};

export const getKpiValueFormat = (kpiId) => KPI_VALUE_FORMAT_BY_ID[kpiId] ?? 'bn';

export const getSelectedCurrency = (selectedAttributeNamesByDimension) => {
  const label = selectedAttributeNamesByDimension?.[DimensionNames.ConversionID]?.[0] || 'USD';
  const match = String(label).match(/^(USD|EUR|GBP)\b/i);
  if (match) {
    return match[1].toUpperCase();
  }
  return String(label).split(/\s+/)[0]?.toUpperCase() || 'USD';
};

export const formatCurrencyLabel = (currencyLabel) => {
  const code = typeof currencyLabel === 'string' && /^(USD|EUR|GBP)$/i.test(currencyLabel.trim())
    ? currencyLabel.trim().toUpperCase()
    : getSelectedCurrency({ [DimensionNames.ConversionID]: [currencyLabel] });
  return code;
};

/** Values from the API are in billions; pick Tn / Bn / Mn from magnitude. */
export const scaleBillionsValue = (valueInBillions) => {
  if (valueInBillions == null || Number.isNaN(Number(valueInBillions))) {
    return { scaledValue: null, unit: 'Bn' };
  }

  const numericValue = Number(valueInBillions);
  const abs = Math.abs(numericValue);

  if (abs >= 1000) {
    return { scaledValue: numericValue / 1000, unit: 'Tn' };
  }
  if (abs >= 1) {
    return { scaledValue: numericValue, unit: 'Bn' };
  }
  if (abs > 0) {
    return { scaledValue: numericValue * 1000, unit: 'Mn' };
  }
  return { scaledValue: 0, unit: 'Bn' };
};

/** Pick one display unit for a bar series from the largest absolute value. */
export const resolveCurrencyDisplayScale = (valuesInBillions) => {
  const finiteValues = (valuesInBillions ?? [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));
  const maxAbs = finiteValues.length
    ? Math.max(...finiteValues.map((value) => Math.abs(value)))
    : 0;
  const { unit } = scaleBillionsValue(maxAbs);
  const divisor = unit === 'Tn' ? 1000 : unit === 'Mn' ? 0.001 : 1;

  return {
    unit,
    toDisplay: (valueInBillions) => {
      if (valueInBillions == null || Number.isNaN(Number(valueInBillions))) {
        return null;
      }
      return Number(valueInBillions) / divisor;
    },
  };
};

/** e.g. USD + Bn -> "($ Bn)" for AUM / Net flows / Net revenues KPI headings. */
export const formatKpiCurrencyHeadingSuffix = (currencyCode, unit = 'Bn') => {
  const code = String(currencyCode ?? 'USD').trim().toUpperCase();
  const symbol = CURRENCY_SYMBOL_BY_CODE[code] ?? code;
  return `(${symbol} ${unit})`;
};

const getAttrId = (item) => Number(item?.attributeId ?? item?.AttributeId);
const getPointX = (point) => Number(point?.xAttributeId ?? point?.XAttributeId);
const getPointY = (point) => Number(point?.yAttributeId ?? point?.YAttributeId);
const getPointValue = (point) => point?.value ?? point?.Value;

const truncateToDecimalPlaces = (value, decimals) => {
  if (!Number.isFinite(value)) {
    return value;
  }
  const factor = 10 ** decimals;
  return Math.trunc(value * factor) / factor;
};

const formatNumberToDecimals = (value, decimals, { truncate = false, decPoint = '.', thousandSep = ',' } = {}) => {
  const normalized = truncate
    ? truncateToDecimalPlaces(value, decimals)
    : value;
  return Highcharts.numberFormat(normalized, decimals, decPoint, thousandSep);
};

export const formatKpiValue = (format, rawValue, options = {}) => {
  if (rawValue == null || Number.isNaN(rawValue)) {
    return '—';
  }
  const { truncate = false } = options;
  if (format === 'percent') {
    return `${formatNumberToDecimals(rawValue * 100, VALUE_DECIMALS, { truncate })}%`;
  }
  if (format === 'bps') {
    return `${formatNumberToDecimals(rawValue, VALUE_DECIMALS, { truncate })}`;
  }
  if (format === 'bn') {
    const { scaledValue } = scaleBillionsValue(Number(rawValue));
    if (scaledValue == null) {
      return '—';
    }
    return formatNumberToDecimals(scaledValue, VALUE_DECIMALS, { truncate });
  }
  return Highcharts.numberFormat(rawValue, VALUE_DECIMALS, '.', ',');
};

export const isValidBarChartValue = (value) => {
  if (value == null) {
    return false;
  }
  return Number.isFinite(Number(value));
};

export const hasRenderableBarChartData = (barData) =>
  (barData ?? []).some((value) => isValidBarChartValue(value));

const parseCalendarYearFromLabel = (label) => {
  const match = String(label ?? '').match(/\d{4}/);
  return match ? parseInt(match[0], 10) : NaN;
};

export const filterBarChartXAttributes = (xAttributes, selectedAttributeNamesByDimension) => {
  const selectedYearLabels = selectedAttributeNamesByDimension?.[DimensionNames.YearID] || [];
  if (selectedYearLabels.length < 2) {
    return xAttributes;
  }

  const calendarYears = selectedYearLabels
    .map(parseCalendarYearFromLabel)
    .filter((year) => Number.isFinite(year));

  if (calendarYears.length === 0) {
    return xAttributes;
  }

  const minYear = Math.min(...calendarYears);
  const maxYear = Math.max(...calendarYears);

  return xAttributes.filter((attribute) => {
    const label = String(attribute?.name ?? attribute?.Name ?? '').trim();
    const year = parseCalendarYearFromLabel(label);
    return Number.isFinite(year) ? year >= minYear && year <= maxYear : true;
  });
};

export const buildBarSeries = (dataPoints, xAttributes, seriesAttributeId) => {
  const barCategories = [];
  const barData = [];

  xAttributes.forEach((attribute) => {
    const yearLabel = String(attribute?.name ?? attribute?.Name ?? '').trim();
    if (!yearLabel) {
      return;
    }

    const point = dataPoints.find(
      (entry) => getPointX(entry) === getAttrId(attribute) && getPointY(entry) === seriesAttributeId
    );

    const value = getPointValue(point);
    barCategories.push(yearLabel);
    barData.push(isValidBarChartValue(value) ? Number(value) : null);
  });

  return { barCategories, barData };
};

export const trimLeadingEmptyBarYears = (barCategories, barData) => {
  let firstDataIndex = 0;
  while (firstDataIndex < barData.length && !isValidBarChartValue(barData[firstDataIndex])) {
    firstDataIndex += 1;
  }

  if (firstDataIndex === 0) {
    return { barCategories, barData };
  }

  return {
    barCategories: barCategories.slice(firstDataIndex),
    barData: barData.slice(firstDataIndex),
  };
};

export const getSelectedYearLabel = (selectedAttributeNamesByDimension) => {
  const years = selectedAttributeNamesByDimension?.[DimensionNames.YearID] || [];
  const sortedYears = [...years].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  if (!sortedYears.length) {
    return '—';
  }
  const start = sortedYears[0];
  const end = sortedYears[sortedYears.length - 1];
  if (sortedYears.length > 1 && start !== end) {
    return `${start} – ${end}`;
  }
  return start;
};

export const getEndYearLabel = (selectedAttributeNamesByDimension) => {
  const years = selectedAttributeNamesByDimension?.[DimensionNames.YearID] || [];
  if (!years.length) {
    return 'period';
  }
  const sortedYears = [...years].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  return sortedYears[sortedYears.length - 1];
};

export const resolveKpiTitleParts = (definition, selectedAttributeNamesByDimension, currencyHeadingSuffix) => {
  // if (definition.id === 9006) {
  //   const year = getEndYearLabel(selectedAttributeNamesByDimension);
  //   return {
  //     primary: definition.name,
  //     secondary: `- ${year}`,
  //   };
  // }

  if (KPI_IDS_WITH_CURRENCY_IN_HEADING.has(definition.id)) {
    return {
      primary: `${definition.name} ${currencyHeadingSuffix}`,
    };
  }

  return { primary: definition.name };
};

export const resolveKpiName = (titleParts) => {
  const { primary, secondary } = titleParts;
  return secondary ? `${primary} ${secondary}` : primary;
};

export const getKpiValueCharCount = (displayValue) =>
  Math.max(String(displayValue ?? '—').replace(/\s/g, '').length, 1);

export const getKpiValueSizeTier = (displayValue) => {
  const length = getKpiValueCharCount(displayValue);
  if (length <= 6) return 'lg';
  if (length <= 9) return 'md';
  if (length <= 12) return 'sm';
  if (length <= 15) return 'xs';
  return 'xxs';
};

const SELECTION_SUMMARY_DEFINITIONS = [
  {
    heading: 'Geography',
    keys: ['GeographyID', DimensionNames.CountryID, 'Geographies', 'Geography'],
    matchKey: (key) => String(key).toLowerCase().includes('geograph'),
  },
  {
    heading: 'Asset Class',
    keys: ['AssetClassID', 'Asset Classes', 'Asset class'],
    matchKey: (key) => {
      const lower = String(key).toLowerCase();
      return lower.includes('assetclass') || lower.includes('asset class');
    },
  },
  {
    heading: 'Client Segment',
    keys: ['SegmentID', 'Client Segments', 'ClientSegmentID'],
    matchKey: (key) => {
      const lower = String(key).toLowerCase();
      return lower.includes('segmentid') || lower.includes('clientsegment');
    },
  },
  {
    heading: 'Vehicle',
    keys: ['VehicleID', 'Vehicle', 'Vehicles'],
    matchKey: (key) => String(key).toLowerCase().includes('vehicle'),
  },
];

const resolveSelectionValues = (selectedAttributeNamesByDimension, definition) => {
  const names = selectedAttributeNamesByDimension ?? {};

  for (const key of definition.keys) {
    const values = names[key];
    if (Array.isArray(values) && values.length > 0) {
      return values;
    }
  }

  const matchedKey = Object.keys(names).find(definition.matchKey);
  const matchedValues = matchedKey ? names[matchedKey] : null;
  return Array.isArray(matchedValues) ? matchedValues : [];
};

const formatSelectionSummaryValue = (values) => {
  const normalizedValues = (values ?? []).map((value) => String(value).trim()).filter(Boolean);
  return normalizedValues.length > 0 ? normalizedValues.join(', ') : '—';
};

export const buildSelectionSummaryRows = (selectedAttributeNamesByDimension) =>
  SELECTION_SUMMARY_DEFINITIONS.map((definition) => ({
    heading: definition.heading,
    value: formatSelectionSummaryValue(
      resolveSelectionValues(selectedAttributeNamesByDimension, definition)
    ),
  }));

const escapeCsvCell = (value) => {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

/**
 * Builds a single combined CSV with all 6 KPIs + the selected bar chart series.
 * Format: Section,Name,Value
 * @see https://api.highcharts.com/highcharts/exporting.csv
 */
export const buildLiveDashboardCombinedCsv = ({
  kpiCards,
  selectedBarChart,
}) => {
  const rows = ['Section,Name,Value'];

  (kpiCards ?? []).forEach((kpi) => {
    const exportValue = kpi.value != null && !Number.isNaN(Number(kpi.value))
      ? kpi.value
      : kpi.displayValue;
    rows.push(['KPI', escapeCsvCell(kpi.name), escapeCsvCell(exportValue)].join(','));
  });

  if (selectedBarChart) {
    selectedBarChart.barCategories.forEach((category, index) => {
      const value = selectedBarChart.barData[index];
      if (value == null || !Number.isFinite(Number(value))) {
        return;
      }
      rows.push(['Bar chart', escapeCsvCell(category), escapeCsvCell(value)].join(','));
    });
  }

  return `${rows.join('\n')}\n`;
};

export const downloadCsvContent = (csv, filename = 'live-dashboard.csv') => {
  if (!csv) {
    return;
  }

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

/** Tabular export payload aligned with Highcharts Dashboards data-pool shape. */
export const buildLiveDashboardExportData = ({
  kpiCards,
  barCharts,
  currency,
  selectedAttributeNamesByDimension,
}) => ({
  kpiTable: {
    columns: ['Metric', 'Display value', 'Raw value', 'Year', 'Currency'],
    rows: kpiCards.map((kpi) => [
      resolveKpiName(kpi.titleParts),
      kpi.displayValue,
      kpi.value,
      kpi.id === 9001
        ? getEndYearLabel(selectedAttributeNamesByDimension)
        : getSelectedYearLabel(selectedAttributeNamesByDimension),
      getKpiValueFormat(kpi.id) === 'bn' ? currency : '',
    ]),
  },
  historicBarTables: Object.fromEntries(
    barCharts.map((barChart) => [
      barChart.id,
      {
        metric: barChart.seriesName,
        columns: ['Year', 'Value'],
        rows: barChart.barCategories.map((year, index) => [
          year,
          barChart.barData[index],
        ]),
      },
    ])
  ),
});

export const buildLiveDashboardModel = ({
  chartData,
  selectedAttributeNamesByDimension,
  currencyCode,
}) => {
  if (!chartData) {
    return null;
  }

  const resolvedCurrencyCode = currencyCode || getSelectedCurrency(selectedAttributeNamesByDimension);

  const dataPoints = chartData.dataPoints || chartData.DataPoints || [];
  const xAttributes = filterBarChartXAttributes(
    chartData.xAttributes || chartData.XAttributes || [],
    selectedAttributeNamesByDimension
  );

  const kpiValuesById = new Map(
    dataPoints
      .filter((point) => KPI_ATTRIBUTE_IDS.includes(getPointY(point)))
      .map((point) => [getPointY(point), getPointValue(point)])
  );

  const kpiCards = KPI_DEFINITIONS.map((definition) => {
    const value = kpiValuesById.get(definition.id) ?? null;
    const valueFormat = getKpiValueFormat(definition.id);
    const currencyHeadingSuffix = KPI_IDS_WITH_CURRENCY_IN_HEADING.has(definition.id)
      ? formatKpiCurrencyHeadingSuffix(
        resolvedCurrencyCode,
        valueFormat === 'bn' ? scaleBillionsValue(value).unit : 'Bn',
      )
      : '';
    const displayUnit = KPI_IDS_WITH_CURRENCY_IN_HEADING.has(definition.id) && valueFormat === 'bn'
      ? scaleBillionsValue(value).unit
      : '';
    const titleParts = resolveKpiTitleParts(definition, selectedAttributeNamesByDimension, currencyHeadingSuffix);
    const name = resolveKpiName(titleParts);
    const displayValue = formatKpiValue(valueFormat, value, {
      truncate: definition.truncateDisplay,
    });
    return {
      ...definition,
      name,
      titleParts,
      currencyHeadingSuffix,
      displayUnit,
      hoverName: definition.hoverName ?? definition.name,
      value,
      displayValue,
      valueCharCount: getKpiValueCharCount(displayValue),
      valueSizeTier: getKpiValueSizeTier(displayValue),
    };
  });

  const barCharts = BAR_CHART_DEFINITIONS.map((definition) => {
    let { barCategories, barData } = buildBarSeries(dataPoints, xAttributes, definition.id);
    ({ barCategories, barData } = trimLeadingEmptyBarYears(barCategories, barData));
    return {
      ...definition,
      barCategories,
      barData,
    };
  });

  return { kpiCards, barCharts };
};
